/**
 * PipeInspectionForm'daki 217 satırlık senkronizasyon useEffect'inin SAF çekirdeği.
 *
 * Girdi: izlenen form değerleri; çıktı: hidden Form.Item'lara yazılacak yama.
 * Yan etkisi yok — form.setFieldsValue çağrısı useInspectionResultsSync
 * hook'unda kalır. Davranış eski :58-270 bloğundan birebir taşındı
 * (yalnız sondaki ölü try/catch alınmadı).
 */
import { encodeWeldResult } from './inspectionPayloadCodec';
import { InspectionPatch } from './types';

export interface BuildInspectionPatchArgs {
  weldingdataIds: Array<string | number> | undefined;
  watchedResults: Record<string, any> | undefined;
  watchedReportNotes: unknown;
  inspectionType: string | undefined;
}

export function buildInspectionPatch({
  weldingdataIds,
  watchedResults,
  watchedReportNotes,
  inspectionType,
}: BuildInspectionPatchArgs): InspectionPatch {
  const ids = Array.isArray(weldingdataIds) ? weldingdataIds.map((w) => String(w)) : [];
  const toCode = (v?: string) => (v === 'FAIL' ? 'F' : v === 'REWORK' ? 'R' : 'P');
  const arr: string[] = ids.map((id) => toCode((watchedResults && watchedResults[id]?.result) as string | undefined));
  // results codes
  const patch: any = { results_codes: arr };
  // fail/rework flags and counts
  const vals = ids.map((id) => (watchedResults && watchedResults[id]?.result) as string | undefined);
  const failCount = vals.filter(v => v === 'FAIL').length;
  const reworkCount = vals.filter(v => v === 'REWORK').length;
  patch.has_fail_or_rework = (failCount + reworkCount) > 0;
  patch.fail_count = failCount;
  patch.rework_count = reworkCount;
  // combined remarks (collect all remarks present)
  // NOT: mevcut davranış gereği yalnız BC/VC/PT/MT remark'ları toplanır (UT/PWHT girmez)
  const remarks: string[] = ids.map((id) => {
    const r = watchedResults?.[id];
    const text = inspectionType === 'BC'
      ? r?.bc?.remarks
      : inspectionType === 'VC'
      ? r?.vc?.remarks
      : inspectionType === 'PT'
      ? r?.pt?.remarks
      : inspectionType === 'MT'
      ? (r as any)?.mt?.remarks
      : undefined;
    const t = (text != null ? String(text) : '').trim();
    return t ? `Weld ${id}: ${t}` : '';
  }).filter(Boolean);
  patch.combined_remarks = remarks.join('\n');
  // pwht hardness list
  if (inspectionType === 'PWHT') {
    const list: number[] = [];
    const outOfRangeWelds: string[] = [];
    ids.forEach((id) => {
      const v = watchedResults?.[id]?.hardness;
      const n = v != null && v !== '' ? Number(v) : NaN;
      if (!Number.isNaN(n)) {
        list.push(n);
        if (!(n >= 0 && n <= 500)) outOfRangeWelds.push(id);
      }
    });
    patch.pwht_hardness_list = list;
    patch.pwht_hardness_out_of_range = outOfRangeWelds.length > 0;
    patch.pwht_hardness_invalid_welds = outOfRangeWelds;
  } else {
    patch.pwht_hardness_list = undefined;
    patch.pwht_hardness_out_of_range = false;
    patch.pwht_hardness_invalid_welds = [];
  }
  // normalized details payloads (verbose for JSON storage)
  patch.details_payloads = ids.map((id) => encodeWeldResult(inspectionType, watchedResults?.[id] || {}));
  // final notes suggestion and explanation requirement
  const parentNotes = (watchedReportNotes != null ? String(watchedReportNotes) : '').trim();
  const combined = String(patch.combined_remarks || '').trim();
  patch.require_fail_rework_explanation = patch.has_fail_or_rework && !combined && !parentNotes;
  patch.final_notes = patch.has_fail_or_rework
    ? [parentNotes, combined].filter(Boolean).join('\n\n') || undefined
    : (parentNotes || undefined);
  return patch as InspectionPatch;
}
